import { useState } from 'react';
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the standard Convex React client package, not a boundary violation
import { api } from '@convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Globe, Eye, EyeOff, Save, Loader2, ChevronDown } from 'lucide-react';
import { usePortalSettings } from '@/hooks/usePortalSettings';
import { cn } from '@/lib/utils';
import type { IPortalSettingsProps } from '@/@types/insurance';

export function PortalSettingsSection({ providerId, orgId, providerName }: IPortalSettingsProps) {
  const [isOpen, setIsOpen] = useState(false);

  const credentials = useQuery(api['insurance/portalCredentials'].getByProvider, {
    orgId,
    providerId,
  });

  const {
    portalUrl,
    setPortalUrl,
    username,
    setUsername,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    isSaving,
    saveMessage,
    handleSave,
  } = usePortalSettings({ orgId, providerId, providerName, credentials });

  return (
    <div className="rounded-lg border p-4">
      <button
        type="button"
        className={cn(
          'flex w-full items-center justify-between text-left',
          'text-sm font-medium text-foreground'
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4" />
          <span>Portal Settings</span>
          {credentials && (
            <span className="text-xs text-green-600 dark:text-green-400">Configured</span>
          )}
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="mt-4 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="portal-url">
              Portal URL
            </label>
            <Input
              id="portal-url"
              value={portalUrl}
              onChange={e => setPortalUrl(e.target.value)}
              placeholder="https://portal-prod-cy.insnemo.com"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="portal-username">
              Username
            </label>
            <Input
              id="portal-username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="email@example.com"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="portal-password">
              Password
            </label>
            <div className="relative">
              <Input
                id="portal-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={
                  credentials?.hasPassword
                    ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'
                    : 'Enter password'
                }
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {credentials?.hasPassword && (
              <p className="text-xs text-muted-foreground">Leave blank to keep existing password</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              size="sm"
              disabled={!portalUrl || !username || isSaving}
              onClick={handleSave}
            >
              {isSaving ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <Save className="mr-1 h-3 w-3" />
              )}
              Save Credentials
            </Button>
            {saveMessage && (
              <span
                className={cn(
                  'text-xs',
                  saveMessage === 'Credentials saved'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-destructive'
                )}
              >
                {saveMessage}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
